import { Controller } from "@hotwired/stimulus"

// Tab strip over panels. A `tab` and a `content` target pair up through the
// same data-tab-index. With the `hash` value on, the chosen tab's data-tab-key
// goes into location.hash, so a reload or a Turbo stream keeps the tab.
// The `active` and `inactive` classes (data-tabs-active-class, …) replace the
// strip's underline defaults, so a vertical list can highlight its entry.
//
// Used by the public events form (one tab per open event) and the texts page.
const ACTIVE = ["border-blue-500", "text-blue-600"]
const INACTIVE = ["border-transparent", "text-gray-500", "hover:text-gray-700", "hover:border-gray-300"]

export default class extends Controller {
  static targets = ["tab", "content"]
  static classes = ["active", "inactive"]
  static values = { defaultTab: { type: Number, default: 0 }, hash: Boolean }

  connect() {
    this.show(this.indexFromHash() ?? this.defaultTabValue)
  }

  // A tab streamed back in (the texts page redraws a nav entry after a save)
  // arrives unstyled. Before the first show there is nothing to match it against.
  tabTargetConnected(tab) {
    if (this.currentIndex === undefined) return
    this.style(tab, Number(tab.dataset.tabIndex) === this.currentIndex)
  }

  switchTab(event) {
    this.show(Number(event.currentTarget.dataset.tabIndex))
  }

  show(index) {
    this.currentIndex = index
    this.tabTargets.forEach((tab) => {
      const selected = Number(tab.dataset.tabIndex) === index
      this.style(tab, selected)
      if (selected && this.hashValue && tab.dataset.tabKey) {
        history.replaceState(history.state, "", `#${tab.dataset.tabKey}`)
      }
    })
    this.contentTargets.forEach((content) => {
      content.classList.toggle("hidden", Number(content.dataset.tabIndex) !== index)
    })
  }

  style(tab, selected) {
    const active = this.hasActiveClass ? this.activeClasses : ACTIVE
    const inactive = this.hasInactiveClass ? this.inactiveClasses : INACTIVE
    tab.classList.remove(...(selected ? inactive : active))
    tab.classList.add(...(selected ? active : inactive))
    tab.setAttribute("aria-selected", selected)
  }

  indexFromHash() {
    if (!this.hashValue || !location.hash) return null
    const key = decodeURIComponent(location.hash.slice(1))
    const tab = this.tabTargets.find((candidate) => candidate.dataset.tabKey === key)
    return tab ? Number(tab.dataset.tabIndex) : null
  }
}
